import React from "react";

export function LeaderboardPlot({ data, task }) {
	if (!data || data.length === 0) return null;

	const allPossibleMetrics = [
		{ key: "Syntax (STX)", label: "STX" },
		{ key: "Functionality", label: "FNC" },
		{ key: "Synthesis (SYN)", label: "SYN" },
		{ key: "Aggregated Score", label: "PSQ" },
		{ key: "EM", label: "EM" },
	];

	const availableMetrics = allPossibleMetrics.filter(m => {
		if (m.key === "Functionality") {
			return task === "Module Completion"
				? data[0]?.["Functionality (EQV)"] != null
				: (data[0]?.["Functionality (FNC)"] != null || data[0]?.["Functionality"] != null);
		}
		if (m.key === "Syntax (STX)") {
			return task === "Rule Completion" 
				? data[0]?.["Syntax"] != null 
				: data[0]?.[m.key] != null;
		}
		return data[0]?.[m.key] != null;
	});

	const metrics = availableMetrics.length > 0 ? availableMetrics : allPossibleMetrics;

	const getMetricValue = (row, metricKey) => {
		if (metricKey === "Functionality") {
			if (task === "Module Completion") return row["Functionality (EQV)"];
			if (task === "Rule Completion") return row["Functionality"];
			return row["Functionality (FNC)"] || row["Functionality"];
		}
		if (metricKey === "Syntax (STX)") {
			if (task === "Rule Completion") return row["Syntax"];
		}
		return row[metricKey];
	};

	const values = data.map(row => ({
		name: task === "Rule Completion" ? `${row.modelName} (${row.promptMode})` : row.modelName,
		points: metrics.map(m => getMetricValue(row, m.key))
	}));

	const allPoints = values.flatMap(v => v.points).filter(p => typeof p === 'number' && !isNaN(p));
	if (allPoints.length === 0) return null;
	const minVal = Math.min(...allPoints);
	const maxVal = Math.max(...allPoints);
	const range = maxVal - minVal || 1;

	const width = 800;
	const height = 300;
	const padding = { top: 20, right: 20, bottom: 40, left: 50 };
	const chartWidth = width - padding.left - padding.right;
	const chartHeight = height - padding.top - padding.bottom;

	const getX = (index) => {
		if (metrics.length <= 1) return padding.left;
		return padding.left + (index * (chartWidth / (metrics.length - 1)));
	};
	const getY = (val) => {
		if (val === undefined || val === null || isNaN(val)) return null;
		return padding.top + chartHeight - ((val - minVal) / range * chartHeight);
	};

	const colors = [
		"#10B981", "#3B82F6", "#F59E0B", "#EF4444", "#8B5CF6", "#EC4899", 
		"#06B6D4", "#F97316", "#A855F7", "#6366F1", "#14B8A6", "#F43F5E"
	];

	if (task === "Line Completion") {
		const sortedData = [...data].sort((a, b) => (b["EM"] || 0) - (a["EM"] || 0));
		const maxEM = Math.max(...sortedData.map(r => r["EM"] || 0), 0);
		const barPadding = { top: 20, right: 60, bottom: 60, left: 150 };
		const barChartWidth = width - barPadding.left - barPadding.right;
		const barChartHeight = height - barPadding.top - barPadding.bottom;
		const barSlotHeight = barChartHeight / sortedData.length;
		const barThickness = barSlotHeight * 0.6;

		return (
			<div className="w-full flex justify-center mb-6">
				<div className="w-full max-w-6xl min-w-6xl flex bg-gray-900/40 border border-gray-700 rounded-lg p-4 shadow-lg backdrop-blur-sm">
					<svg 
						viewBox={`0 0 ${width} ${height}`} 
						className="overflow-visible w-full h-auto"
					>
						<line x1={barPadding.left} y1={barPadding.top} x2={barPadding.left} y2={barPadding.top + barChartHeight} stroke="#374151" strokeWidth="1" />
						<line x1={barPadding.left} y1={barPadding.top + barChartHeight} x2={barPadding.left + barChartWidth} y2={barPadding.top + barChartHeight} stroke="#374151" strokeWidth="1" />
						
						{[0, 0.25, 0.5, 0.75, 1].map(tick => {
							const x = barPadding.left + (tick * barChartWidth);
							const tickVal = tick * maxEM;
							return (
								<g key={tick} transform={`translate(${x},${barPadding.top + barChartHeight + 20})`}>
									<text textAnchor="middle" className="text-[10px] fill-gray-500 font-medium">
										{tickVal.toFixed(1)}
									</text>
								</g>
							);
						})}

						{sortedData.map((row, i) => {
							const val = row["EM"] || 0;
							const barWidth = maxEM > 0 ? (val / maxEM) * barChartWidth : 0;
							const y = barPadding.top + (i * barSlotHeight) + (barSlotHeight - barThickness) / 2;
							const color = colors[i % colors.length];
							
							return (
								<g key={row.modelName}>
									<rect 
										x={barPadding.left} 
										y={y} 
										width={barWidth} 
										height={barThickness} 
										fill={color} 
										rx="2"
									/>
									<text 
										x={barPadding.left - 10} 
										y={y + barThickness / 2 + 4} 
										textAnchor="end" 
										className="text-[10px] fill-gray-400 font-medium"
									>
										{row.modelName}
									</text>
									<text 
										x={barPadding.left + barWidth + 5} 
										y={y + barThickness / 2 + 4} 
										className="text-[10px] fill-gray-300 font-bold"
									>
										{maxEM > 1 ? `${val.toFixed(1)}%` : `${(val * 100).toFixed(1)}%`}
									</text>
								</g>
							);
						})}
					</svg>
				</div>
			</div>
		);
	}

	return (
		<div className="w-full flex justify-center mb-6">
			<div className="w-full max-w-6xl min-w-6xl flex gap-4 bg-gray-900/40 border border-gray-700 rounded-lg p-4 shadow-lg backdrop-blur-sm">
				<svg width={width} height={height} className="overflow-visible w-full h-auto">
					{/* Grid Lines */}
					<line x1={padding.left} y1={padding.top} x2={padding.left} y2={padding.top + chartHeight} stroke="#374151" strokeWidth="1" />
					<line x1={padding.left} y1={padding.top + chartHeight} x2={padding.left + chartWidth} y2={padding.top + chartHeight} stroke="#374151" strokeWidth="1" />
					
					{/* Y Axis Labels */}
					{[0, 0.25, 0.5, 0.75, 1].map(tick => {
						const y = getY(minVal + tick * range);
						return (
							<g key={tick} transform={`translate(${padding.left - 10},${y})`}>
								<text textAnchor="end" className="text-[10px] fill-gray-500 font-medium">
									{ (minVal + tick * range).toFixed(1) }
								</text>
								<line x1={padding.left - 5} y1={0} x2={padding.left} y2={0} stroke="#374151" strokeWidth="1" />
							</g>
						);
					})}

					{/* X Axis Labels */}
					{metrics.map((m, i) => (
						<text 
							key={m.label} 
							x={getX(i)} 
							y={padding.top + chartHeight + 20} 
							textAnchor="middle" 
							className="text-[12px] fill-gray-400 font-semibold"
						>
							{m.label}
						</text>
					))}

					{/* Lines and Markers */}
					{values.map((v, i) => {
						const color = colors[i % colors.length];
						const points = v.points.map((p, idx) => {
							const x = getX(idx);
							const y = getY(p);
							return y !== null ? { x, y } : null;
						}).filter(p => p !== null);

						const polylinePoints = points.map(p => `${p.x},${p.y}`).join(" ");

						return (
								<g key={`${v.name}-${i}`}>
									<polyline 
										points={polylinePoints} 
										fill="none" 
										stroke={color} 
										strokeWidth="1.5" 
										strokeLinejoin="round" 
										strokeLinecap="round"
									/>
									{points.map((p, idx) => (
										<circle 
											key={`dot-${idx}`} 
											cx={p.x} 
											cy={p.y} 
											r="2.5" 
											fill={color} 
											stroke="#111827" 
											strokeWidth="1"
										/>
									))}
								</g>
						);
					})}
				</svg>

				<div className="flex flex-col gap-2 max-h-[300px] overflow-y-auto pr-2 custom-scrollbar min-w-[180px]">
					<p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1">Models</p>
					{values.map((v, i) => (
								<div key={`${v.name}-${i}`} className="flex items-center gap-2">
									<div 
										className="w-3 h-0.5 rounded-full" 
										style={{ backgroundColor: colors[i % colors.length] }}
									/>
									<span className="text-[10px] text-gray-300 whitespace-nowrap">{v.name}</span>
								</div>
					))}
				</div>
			</div>
		</div>
	);
}
